"use client";

import Link from "next/link";
import { useMemo } from "react";
import type { CategoryStructure } from "@/lib/categoryStructure";
import {
  getInactiveCategoryPreview,
  getInactiveSubcategoriesByCategory,
} from "@/lib/listing/inactiveCategoryPreview";

export interface SellCategoryAvailabilityNoticeProps {
  /** Active categories/subcategories shown in the picker. */
  categoryStructure: CategoryStructure[];
}

export default function SellCategoryAvailabilityNotice({
  categoryStructure,
}: SellCategoryAvailabilityNoticeProps) {
  const inactiveGroups = useMemo(
    () => getInactiveSubcategoriesByCategory(categoryStructure),
    [categoryStructure],
  );

  const { previewRows, hiddenCategoryCount } = useMemo(
    () => getInactiveCategoryPreview(inactiveGroups),
    [inactiveGroups],
  );

  if (inactiveGroups.length === 0) {
    return null;
  }

  return (
    <div className="mt-3 rounded-lg border border-border bg-surface p-3">
      <p className="text-sm font-semibold text-foreground">
        Only available categories are accepted right now
      </p>
      <p className="mt-1 text-xs text-text-light">
        Choose from the dropdown below — it only includes categories we can list
        today. If your item isn&apos;t there, please don&apos;t list it yet.
      </p>
      <p className="mt-3 text-xs font-medium text-text-light">
        Examples of what we can&apos;t accept yet:
      </p>
      <ul className="mt-1 space-y-1.5 text-sm text-foreground">
        {previewRows.map((c) => (
          <li key={c.categoryName}>
            <span className="font-medium">{c.categoryName}:</span>{" "}
            {c.subcategoryNames.join(", ")}
          </li>
        ))}
      </ul>
      {hiddenCategoryCount > 0 ? (
        <p className="mt-2 text-xs text-text-light">
          and {hiddenCategoryCount} more categor
          {hiddenCategoryCount === 1 ? "y" : "ies"} not listed here
        </p>
      ) : null}
      <p className="mt-3 text-xs text-text-light">
        See the full accepted and prohibited lists in our{" "}
        <Link href="/faq" className="text-blue-500 hover:underline">
          FAQ
        </Link>{" "}
        or{" "}
        <Link href="/terms" className="text-blue-500 hover:underline">
          Terms of Service
        </Link>
        .
      </p>
    </div>
  );
}
